/**
 * Stage 1a: Generate Keywords — context-based version using StagePanel.
 *
 * Generates keyword suggestions from the thesis. LLM stage.
 * Thesis editing goes through the document editor.
 */

import React, { useState } from 'react';
import { StagePanel } from '../StagePanel';
import { useArticleContext } from '../ArticleContext';
import type { ArticleDocument } from '../types';

function ThesisSummary() {
  const { document } = useArticleContext();
  const t = document?.thesis;
  if (!t) return null;
  return (
    <div className="text-xs text-gray-600 space-y-1 bg-gray-50 rounded border p-2">
      <div><span className="text-gray-400">Name:</span> {t.candidate_name}</div>
      <div><span className="text-gray-400">ICP:</span> {t.icp}</div>
      <div><span className="text-gray-400">Funnel:</span> {t.funnel_stage} | <span className="text-gray-400">Focus:</span> {t.focus}</div>
      {t.content_pillar && <div><span className="text-gray-400">Pillar:</span> {t.content_pillar}</div>}
      {t.business_context && <div><span className="text-gray-400">Context:</span> {t.business_context}</div>}
      {t.directional_guidance && <div><span className="text-gray-400">Direction:</span> {t.directional_guidance}</div>}
    </div>
  );
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className="text-xs text-blue-600 hover:text-blue-800 font-medium"
      onClick={() => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }}
    >
      {copied ? 'Copied!' : 'Copy all'}
    </button>
  );
}

function renderOutput(doc: ArticleDocument) {
  const suggestions = doc.targeting?.keywords?.suggestions;
  if (!suggestions) {
    return <div className="text-sm text-gray-500">No keyword suggestions yet.</div>;
  }
  const sections = [
    { label: 'Short keywords', items: suggestions.short },
    { label: 'Medium keywords', items: suggestions.medium },
    { label: 'Long-tail keywords', items: suggestions.long_tail },
  ].filter((s) => s.items && s.items.length > 0);

  const allKeywords = sections.flatMap((s) => s.items);

  return (
    <div className="space-y-2">
      <div className="flex justify-end">
        <CopyButton text={allKeywords.join('\n')} />
      </div>
      {sections.map((s) => (
        <div key={s.label}>
          <div className="text-xs font-medium text-gray-700 mb-1">{s.label}</div>
          <div className="flex flex-wrap gap-1">
            {s.items.map((kw, i) => (
              <span key={i} className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded">
                {kw}
              </span>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export function Stage1aKeywordsV2() {
  return (
    <StagePanel
      stageId="stage_1a_keywords"
      isLlmStage
      title="Stage 1a: Generate Keywords"
      description="Generates keyword suggestions from the article thesis."
      renderOutput={renderOutput}
    >
      <ThesisSummary />
    </StagePanel>
  );
}
